import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Building2, AlertTriangle, ChevronRight, ArrowLeft, Shield } from 'lucide-react';

const C = { navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C' };

export default function TerritoryPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/analytics/territory').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  const selectState = async (state) => {
    setSelectedState(state);
    const r = await api.get(`/analytics/territory/${encodeURIComponent(state)}`);
    setDetail(r.data);
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="territory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>Territory Analytics</h1>
          {selectedState ? (
            <div className="flex items-center gap-1.5 text-sm mt-1">
              <button onClick={() => { setSelectedState(null); setDetail(null); }} className="hover:underline" style={{ color: C.blue }}>All Regions</button>
              <ChevronRight className="w-3.5 h-3.5" style={{ color: C.slate }} />
              <span style={{ color: C.navy }} className="font-medium">{selectedState}</span>
            </div>
          ) : (
            <p className="text-sm mt-1" style={{ color: C.slate }}>
              {(data.mapped_entities || data.total_entities).toLocaleString()} register businesses mapped to regions — simulated ANZ market universe (market intelligence, not your insured book)
            </p>
          )}
        </div>
        {selectedState && (
          <button onClick={() => { setSelectedState(null); setDetail(null); }} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium hover:bg-[rgba(26,76,124,0.08)]" style={{ color: C.blue }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
      </div>

      {!selectedState ? (
        <>
          {/* Overview KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Register Businesses" value={(data.total_entities || data.mapped_entities).toLocaleString()} color={C.blue} />
            <KPI label="Australia" value={data.au_count.toLocaleString()} color={C.navy} />
            <KPI label="New Zealand" value={data.nz_count.toLocaleString()} color={C.teal} />
            <KPI label="Hazard Zones" value={`${data.hazard_distribution?.high || 0} high`} color={C.red} />
          </div>

          {/* States Table */}
          <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
              <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Regions</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="territory-table">
                <thead>
                  <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                    {['Region', 'Entities', 'Share', 'Avg Hazard', 'Avg Crime', 'Claims Flagged', ''].map(h => (
                      <th key={h} className="text-left py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.by_state.map(s => (
                    <tr key={s.state} onClick={() => selectState(s.state)} className="border-b border-[rgba(136,150,168,0.08)] hover:bg-[#F5F7FA] cursor-pointer group" data-testid={`state-row-${s.state}`}>
                      <td className="py-2 px-3"><div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" style={{ color: C.blue }} /><span className="text-xs font-medium" style={{ color: C.navy }}>{s.state}</span></div></td>
                      <td className="py-2 px-3 text-xs font-mono" style={{ color: C.navy }}>{s.entity_count.toLocaleString()}</td>
                      <td className="py-2 px-3"><div className="flex items-center gap-2"><div className="w-16 h-1.5 rounded-full" style={{ background: C.bg }}><div className="h-1.5 rounded-full" style={{ width: `${Math.min(s.pct, 100)}%`, background: C.blue }} /></div><span className="text-[10px]" style={{ color: C.slate }}>{s.pct}%</span></div></td>
                      <td className="py-2 px-3 text-xs font-mono" style={{ color: s.avg_hazard > 40 ? C.red : s.avg_hazard > 25 ? C.amber : C.green }}>{s.avg_hazard}</td>
                      <td className="py-2 px-3 text-xs font-mono" style={{ color: s.avg_crime > 40 ? C.red : C.slate }}>{s.avg_crime}</td>
                      <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{s.claims_flagged.toLocaleString()}</td>
                      <td className="py-1 px-2"><ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: C.blue }} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Top Industries */}
          <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <h3 className="text-sm font-semibold mb-3 uppercase tracking-wider" style={{ color: C.navy }}>Top Industries</h3>
            <div className="space-y-2">
              {data.top_industries.slice(0, 12).map((ind, i) => (
                <div key={ind.industry} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-[10px] w-4 text-right" style={{ color: C.slate }}>{i + 1}</span>
                    <span className="text-xs" style={{ color: C.navy }}>{ind.industry}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 rounded-full" style={{ background: C.bg }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${Math.min((ind.count / data.top_industries[0].count) * 100, 100)}%`, background: C.teal }} />
                    </div>
                    <span className="text-xs font-mono w-12 text-right" style={{ color: C.slate }}>{ind.count.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : detail && (
        <>
          {/* State Detail */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Entities" value={detail.total_entities.toLocaleString()} color={C.blue} />
            <KPI label="Avg Hazard" value={detail.avg_hazard} color={detail.avg_hazard > 40 ? C.red : C.green} />
            <KPI label="Avg Crime" value={detail.avg_crime} color={detail.avg_crime > 40 ? C.red : C.green} />
            <KPI label="Claims Rate" value={`${detail.claims_rate}%`} color={detail.claims_rate > 15 ? C.red : C.green} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
              <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Industries in {selectedState}</h3>
              <div className="space-y-1.5">
                {detail.industries.map(ind => (
                  <div key={ind.industry} className="flex items-center justify-between py-1">
                    <span className="text-xs" style={{ color: C.navy }}>{ind.industry}</span>
                    <span className="text-xs font-mono" style={{ color: C.slate }}>{ind.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
              <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Cities in {selectedState}</h3>
              <div className="space-y-1.5">
                {detail.cities.map(city => (
                  <div key={city.city} className="flex items-center justify-between py-1">
                    <span className="text-xs" style={{ color: C.navy }}>{city.city}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px]" style={{ color: city.avg_hazard > 40 ? C.red : C.slate }}>Hazard: {city.avg_hazard}</span>
                      <span className="text-xs font-mono" style={{ color: C.slate }}>{city.count.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}
